/**
 * The Permit step on a Mountain Brook job (ADR 0289).
 *
 * Jonathon, 2026-10-15: "we just need the pink form filled out whenever the
 * city is mountainbrook and we click on the permit button. then that goes into
 * docs once it's filled out. then we need to be able to click on the jot form
 * link".
 *
 * So the step opens the city's pink application already filled from the job.
 * The office checks it, typing straight onto the paper where something is
 * wrong, and saves it to Docs as a PDF. The city's white certification, on
 * JotForm, is one press from here.
 *
 * Saving does not tick the step: the permit is real once the city issues it,
 * and the office ticks it then, from here, with Mark complete.
 */

import {
  MOUNTAIN_BROOK_PERMIT_FORM_URL,
  permitFileName,
  type Job,
  type JobChecklistItem,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../../components/ui";
import { t } from "../../../i18n";
import { useAsync } from "../../../lib/hooks";
import { permitPdfFile } from "../../../lib/permitPdf";
import { fetchPermitApplication } from "../../../lib/permitQueries";
import { uploadJobDocument } from "../../../lib/queries";
import { useSession } from "../../../session";
import { permitEditor } from "./PermitFields";
import { PermitPinkSheet } from "./PermitPinkSheet";

type Status = "pending" | "done" | "na";

export function PermitModal({
  job,
  item,
  onClose,
  onSetStatus,
}: {
  job: Job;
  item: JobChecklistItem;
  onClose: () => void;
  onSetStatus: (status: Status) => Promise<void>;
}) {
  const { profileId } = useSession();
  const form = useAsync(() => fetchPermitApplication(job), [job.id]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The file name it was saved under, once it has been. */
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    if (!form.data) return;
    setBusy(true);
    setError(null);
    try {
      const name = permitFileName(form.data);
      await uploadJobDocument(
        job.id,
        await permitPdfFile(form.data, name),
        profileId ?? null,
      );
      setSaved(name);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function mark(status: Status) {
    setBusy(true);
    setError(null);
    try {
      await onSetStatus(status);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const cityForm = MOUNTAIN_BROOK_PERMIT_FORM_URL;

  return (
    <Modal
      title={t("Mountain Brook permit")}
      subtitle={t("Check it over. Every box in blue can be typed into.")}
      onClose={onClose}
      wide
      actions={
        <>
          <StatusButtons status={item.status} busy={busy} onMark={mark} />
          {cityForm ? (
            <button
              type="button"
              className={saved ? "primary" : undefined}
              onClick={() => window.open(cityForm, "_blank", "noopener")}
            >
              {t("Open the city's JotForm")}
            </button>
          ) : null}
          {saved ? null : (
            <button
              type="button"
              className="primary"
              disabled={busy || !form.data}
              onClick={() => void save()}
            >
              {busy ? t("Saving…") : t("Save to Docs")}
            </button>
          )}
        </>
      }
    >
      <ErrorNote error={error ?? form.error} />
      {saved ? (
        <p className="n" role="status">
          {t("Saved to Docs as “{name}”.", { name: saved })}
          {cityForm
            ? ` ${t("Next, the city's certification on JotForm.")}`
            : ""}
        </p>
      ) : null}
      {form.data ? (
        <div className="kdoc kdoc-edit">
          <div className="kdoc-sheets">
            <PermitPinkSheet
              ed={permitEditor(form.data, (next) => {
                form.setData(next);
                // A change after saving is a different form from the one in
                // Docs, so it is saved again rather than left behind.
                setSaved(null);
              })}
            />
          </div>
        </div>
      ) : form.loading ? (
        <p className="n">{t("Filling it in from the job…")}</p>
      ) : null}
    </Modal>
  );
}

/** Not needed, complete, or back to not done: whichever it is not now. */
function StatusButtons({
  status,
  busy,
  onMark,
}: {
  status: string;
  busy: boolean;
  onMark: (status: Status) => Promise<void>;
}) {
  const choices: [Status, string][] = [
    ["na", "Permit not needed"],
    ["done", "Mark complete"],
    ["pending", "Not done yet"],
  ];
  return (
    <>
      {choices
        .filter(([value]) => value !== status)
        .map(([value, label]) => (
          <button
            key={value}
            type="button"
            disabled={busy}
            onClick={() => void onMark(value)}
          >
            {t(label)}
          </button>
        ))}
    </>
  );
}
